import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#f8f9fa]">
      <div className="hidden lg:flex flex-col justify-between flex-1 bg-slate-900 text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.08) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.06) 0, transparent 50%)",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 bg-white text-slate-900 flex items-center justify-center rounded-sm">
            <span className="font-display text-lg font-bold">G</span>
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight">GND Audit</div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
              Lightspeed Provisionsanalyse
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-md space-y-6">
          <div className="kbd-label text-slate-400">Was Sie erwartet</div>
          <h2 className="font-display text-4xl font-bold leading-tight tracking-tight">
            Volle Kontrolle über Ihre Lightspeed-Provisionen.
          </h2>
          <p className="text-slate-300 leading-relaxed text-sm">
            Lade jeden Monat Deine Provisionsberichte hoch und vergleiche Soll- mit
            Ist-Werten. Erkenne fehlende Kunden, abweichende Provisionssätze und reklamiere
            direkt aus der Anwendung.
          </p>
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="border-t border-slate-700 pt-3">
              <div className="font-display text-2xl font-bold">30 %</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400">
                Standard-Provision
              </div>
            </div>
            <div className="border-t border-slate-700 pt-3">
              <div className="font-display text-2xl font-bold">Real-Time</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-400">
                IMAP-Postbox
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-[11px] uppercase tracking-[0.12em] text-slate-500 font-semibold">
          © 2026 Gastro Next Digital UG
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm space-y-7"
          data-testid="login-form"
        >
          <div>
            <div className="kbd-label mb-2 flex items-center gap-1.5 text-slate-500">
              <ShieldCheck size={14} strokeWidth={1.8} /> Anmeldung
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              Willkommen zurück.
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Bitte melde Dich mit Deinen Zugangsdaten an.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="admin@gastro-beratung.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Passwort</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <div
              className="border border-rose-200 bg-rose-50 text-rose-700 text-sm rounded-sm px-3 py-2"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-11"
            data-testid="login-submit-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Anmelden
          </Button>

          <div className="border-t border-slate-200 pt-4 text-xs text-slate-500 leading-relaxed">
            Standard-Zugang ist in <code className="font-mono text-slate-700">backend/.env</code>{" "}
            hinterlegt. Passwort und Empfänger-Adressen lassen sich nach dem Login unter
            «Einstellungen» anpassen.
          </div>
        </form>
      </div>
    </div>
  );
}
